import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'

import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import { HOMES_AND_VILLAS_FILTERS } from 'constants/offerList'
import useOfferList from 'hooks/Offers/useOfferList'
import { useAppSelector } from 'hooks/reduxHooks'
import useStableObjectReference from 'hooks/useStableObjectReference'
import getOfferListKey from 'lib/offer/offerListKey'
import { without } from 'lib/array/arrayUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'

const NEARBY_SINGLE_UNIT_LIMIT = 10
const NEARBY_SINGLE_UNIT_SEARCH_LIMIT = 12
const HOMES_AND_VILLAS_SEARCH_PATH = '/search/homes-and-villas'

interface Props {
  offer: App.HotelOffer
  property: App.Property.Property
  checkInDate?: string
  checkOutDate?: string
  rooms: Array<App.Occupants>
  propertyTypes?: Array<string>
  disabled?: boolean
}

function PropertyUnavailableSingleUnitRecommendations(props: Props) {
  const {
    offer,
    property,
    checkInDate,
    checkOutDate,
    rooms,
    propertyTypes,
    disabled,
  } = props
  const intl = useIntl()
  const stableRooms = useStableObjectReference(rooms)

  const filters = useMemo<App.OfferListFilters | undefined>(() => {
    if (disabled) return undefined

    return {
      ...HOMES_AND_VILLAS_FILTERS,
      propertyId: property.id,
      ...(propertyTypes?.length ? { propertyTypes } : {}),
      checkIn: checkInDate,
      checkOut: checkOutDate,
      rooms: stableRooms,
      isStream: true,
      include: ['nearby'],
      limit: NEARBY_SINGLE_UNIT_SEARCH_LIMIT,
      showDistanceWithMetaOfferId: offer.id,
    }
  }, [checkInDate, checkOutDate, disabled, offer.id, property.id, propertyTypes, stableRooms])

  const offerList = useOfferList(filters, {
    disabled: !filters,
    excludedIds: [offer.id],
    minRequiredOffers: NEARBY_SINGLE_UNIT_SEARCH_LIMIT,
  })

  const offerListKey = useMemo(() => filters ? getOfferListKey(filters) : undefined, [filters])
  const offerMetaData = useAppSelector(state => offerListKey ? state.offer.searchResultMetadata.offerMetaData[offerListKey] : undefined)

  const nearbyOfferIds = useMemo(() => {
    const offerIds = without(offerList.offerIds, offer.id)
    return offerIds.slice(0, NEARBY_SINGLE_UNIT_LIMIT)
  }, [offer.id, offerList.offerIds])

  const offerDistances = useMemo(() => {
    return nearbyOfferIds.reduce<Record<string, number | undefined>>((acc, offerId) => {
      acc[offerId] = offerMetaData?.[offerId]?.distance
      return acc
    }, {})
  }, [nearbyOfferIds, offerMetaData])

  const overrideOfferList = useMemo<App.OfferList>(() => ({
    ...offerList,
    offerIds: nearbyOfferIds,
  }), [nearbyOfferIds, offerList])

  const allLink = useMemo(() => {
    const searchParams = encodeSearchParams({
      searchItem: {
        searchType: 'property',
        value: property.id,
        format: {
          mainText: property.name,
          secondaryText: '',
        },
      },
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms: stableRooms,
      filters: {
        propertyTypes,
      },
    })

    return `${HOMES_AND_VILLAS_SEARCH_PATH}?${searchParams.toString()}`
  }, [checkInDate, checkOutDate, property.id, property.name, propertyTypes, stableRooms])

  if (!filters || (!overrideOfferList.fetching && overrideOfferList.offerIds.length === 0)) {
    return null
  }

  const title = intl.formatMessage({
    id: 'gu0a5XvPRTZXdL1KYPX5',
    defaultMessage: 'Similar properties with availability',
    description: 'Carousel heading for available nearby single-unit properties shown when a property page is unavailable',
  })

  return <OfferRecommendations
    title={title}
    offerId={offer.id}
    filters={filters}
    offerDistances={offerDistances}
    offerDistanceFrom={property.name}
    trackingDescription="Unavailable SPP Single Unit"
    overrideOfferList={overrideOfferList}
    maxOffers={NEARBY_SINGLE_UNIT_LIMIT}
    allLink={allLink}
    enableDateCheckingForPriceDisplay
  />
}

export default memo(PropertyUnavailableSingleUnitRecommendations)
